import { useEffect, useMemo, useState } from 'react'
import Modal from 'react-modal'
import { EventRsvpBlastAudienceDto, EventRsvpBlastResultDto, VenueSpecialDto } from '@ivy-monorepo/shared'
import { adminService } from '../../services/admin.service'
import ToggleInput from '../ui/ToggleInput'

const TITLE_MAX_LENGTH = 65
const MESSAGE_MAX_LENGTH = 240

interface EventRsvpBlastModalProps {
  isOpen: boolean
  onClose: () => void
  /** The venue's events: upcoming ones first, then ended ones (most recent first). */
  events: VenueSpecialDto[]
  /** Event to start with (e.g. the one the RSVP list is filtered to). */
  initialEventPublicId?: string | null
}

type Stage = 'compose' | 'confirm' | 'done'

const formatEventDate = (event: VenueSpecialDto) =>
  event.eventStartDate ? ` (${new Date(event.eventStartDate).toLocaleDateString()})` : ''

/** Outside its spotlight window the app can't resolve the event, so the push opens the Events tab only. */
const hasEnded = (event: VenueSpecialDto, now: Date) => !!event.eventEndDate && new Date(event.eventEndDate) <= now

const isOutsideSpotlight = (event: VenueSpecialDto, now: Date) => {
  const start = event.startDate ? new Date(event.startDate) : null
  const end = event.endDate ? new Date(event.endDate) : null
  return (start !== null && start > now) || (end !== null && end < now)
}

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`

/**
 * Push to everyone who RSVP'd to one event, deep-linking to the event card (and its Buy Ticket
 * path). Sends right away after a confirm step; every push is logged as `event_rsvp_blast`.
 */
const EventRsvpBlastModal = ({ isOpen, onClose, events, initialEventPublicId }: EventRsvpBlastModalProps) => {
  const [eventPublicId, setEventPublicId] = useState('')
  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')
  const [excludeTicketHolders, setExcludeTicketHolders] = useState(true)
  const [includeInternal, setIncludeInternal] = useState(false)
  const [audience, setAudience] = useState<EventRsvpBlastAudienceDto | null>(null)
  const [isLoadingAudience, setIsLoadingAudience] = useState(false)
  const [stage, setStage] = useState<Stage>('compose')
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<EventRsvpBlastResultDto | null>(null)

  const { upcomingEvents, pastEvents } = useMemo(() => {
    const now = new Date()
    const startOf = (event: VenueSpecialDto) => new Date(event.eventStartDate ?? 0).getTime()
    return {
      upcomingEvents: events.filter(event => !hasEnded(event, now)).sort((a, b) => startOf(a) - startOf(b)),
      pastEvents: events.filter(event => hasEnded(event, now)).sort((a, b) => startOf(b) - startOf(a)),
    }
  }, [events])
  const eventOptions = useMemo(() => [...upcomingEvents, ...pastEvents], [upcomingEvents, pastEvents])

  const selectedEvent = eventOptions.find(event => event.publicId === eventPublicId) ?? null

  useEffect(() => {
    if (!isOpen) return
    const initial = eventOptions.find(event => event.publicId === initialEventPublicId) ?? eventOptions[0]
    setEventPublicId(initial?.publicId ?? '')
    setTitle('')
    setMessage('')
    setExcludeTicketHolders(true)
    setIncludeInternal(false)
    setAudience(null)
    setStage('compose')
    setError(null)
    setResult(null)
    // eventOptions intentionally left out: only reset when the modal opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialEventPublicId])

  useEffect(() => {
    if (!isOpen || !eventPublicId) {
      setAudience(null)
      return
    }
    let cancelled = false
    setIsLoadingAudience(true)
    adminService
      .getEventRsvpBlastAudience(eventPublicId, { excludeTicketHolders, includeInternal })
      .then(data => {
        if (!cancelled) setAudience(data)
      })
      .catch((err: any) => {
        if (!cancelled) {
          setAudience(null)
          setError(err?.friendlyMessage || err?.message || 'Could not load the RSVP list.')
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingAudience(false)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, eventPublicId, excludeTicketHolders, includeInternal])

  const recipientCount = audience?.recipientCount ?? 0
  const canContinue =
    !!selectedEvent && !!title.trim() && !!message.trim() && !isLoadingAudience && recipientCount > 0

  const handleSend = async () => {
    if (!selectedEvent || isSending) return
    setIsSending(true)
    setError(null)
    try {
      const sent = await adminService.sendEventRsvpBlast(selectedEvent.publicId, {
        title: title.trim(),
        message: message.trim(),
        excludeTicketHolders,
        includeInternal,
      })
      setResult(sent)
      setStage('done')
    } catch (err: any) {
      setError(err?.friendlyMessage || err?.message || 'Failed to send the blast. Please try again.')
      setStage('compose')
    } finally {
      setIsSending(false)
    }
  }

  const breakdown = audience
    ? [
        plural(audience.rsvpCount, 'RSVP'),
        audience.excludedTicketHolders > 0 && `${audience.excludedTicketHolders} already bought a ticket`,
        audience.excludedInactive > 0 && `${audience.excludedInactive} deleted / inactive`,
        audience.excludedInternal > 0 && `${audience.excludedInternal} internal`,
        `${audience.reachableCount} of ${audience.recipientCount} have push on`,
      ]
        .filter(Boolean)
        .join(' · ')
    : null

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={isSending ? undefined : onClose}
      ariaHideApp={false}
      className="modal-popup blast-popup event-rsvp-blast-popup"
      overlayClassName="modal-overlay"
      shouldCloseOnEsc={!isSending}
      shouldCloseOnOverlayClick={!isSending}
      onAfterClose={() => {
        document.body.classList.remove('ReactModal__Body--open')
      }}
    >
      <div className="blast-modal-content modal-content-wrapper">
        <h2>Send blast to RSVPs</h2>

        {error && <div className="error-message">{error}</div>}

        {stage === 'done' && result ? (
          <>
            <div className="modal-body-content">
              <p>
                Sent to <strong>{plural(result.sentCount, 'person')}</strong> who RSVP'd to{' '}
                <strong>{selectedEvent?.name ?? audience?.eventName}</strong>.
              </p>
              {(result.skippedCount > 0 || result.failedCount > 0) && (
                <p className="event-rsvp-blast-note">
                  {result.skippedCount > 0 && `${result.skippedCount} skipped (push turned off). `}
                  {result.failedCount > 0 && `${result.failedCount} failed to deliver.`}
                </p>
              )}
            </div>
            <div className="modal-footer-content">
              <button className="styled-button" onClick={onClose}>
                Done
              </button>
            </div>
          </>
        ) : stage === 'confirm' && selectedEvent ? (
          <>
            <div className="modal-body-content">
              <p>
                Send this push to <strong>{plural(recipientCount, 'person')}</strong> who RSVP'd to{' '}
                <strong>{selectedEvent.name}</strong>? It goes out right away and can't be undone.
              </p>
              <div className="event-rsvp-blast-preview">
                <strong>{title.trim()}</strong>
                <span>{message.trim()}</span>
              </div>
              <p className="event-rsvp-blast-note">Tapping it opens the event in the app.</p>
            </div>
            <div className="modal-footer-content">
              <button className="styled-button cancel" onClick={() => setStage('compose')} disabled={isSending}>
                Back
              </button>
              <button className="styled-button" onClick={handleSend} disabled={isSending}>
                {isSending ? 'Sending…' : `Yes, send to ${plural(recipientCount, 'RSVP')}`}
              </button>
            </div>
          </>
        ) : (
          <>
            <form className="modal-body-content" onSubmit={e => e.preventDefault()}>
              <div className="form-group">
                <label>Event</label>
                {eventOptions.length === 0 ? (
                  <p>This venue has no events.</p>
                ) : (
                  <select value={eventPublicId} onChange={e => setEventPublicId(e.target.value)}>
                    {[
                      { label: 'Upcoming', items: upcomingEvents },
                      { label: 'Ended', items: pastEvents },
                    ]
                      .filter(group => group.items.length > 0)
                      .map(group => (
                        <optgroup key={group.label} label={group.label}>
                          {group.items.map(event => (
                            <option key={event.publicId} value={event.publicId}>
                              {event.name}
                              {formatEventDate(event)}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                  </select>
                )}
                {selectedEvent && hasEnded(selectedEvent, new Date()) ? (
                  <p className="event-rsvp-blast-warning">
                    This event has ended. The push still goes to everyone who RSVPed, and opens the Events tab.
                  </p>
                ) : selectedEvent && isOutsideSpotlight(selectedEvent, new Date()) && (
                  <p className="event-rsvp-blast-warning">
                    This event is outside its spotlight window, so the app can't open it — the push will
                    land on the Events tab instead.
                  </p>
                )}
              </div>
              <div className="form-group">
                <label>
                  Title <span className="event-rsvp-blast-counter">{title.length}/{TITLE_MAX_LENGTH}</span>
                </label>
                <input
                  type="text"
                  value={title}
                  maxLength={TITLE_MAX_LENGTH}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="e.g. Tickets are going fast 🎟️"
                />
              </div>
              <div className="form-group">
                <label>
                  Message <span className="event-rsvp-blast-counter">{message.length}/{MESSAGE_MAX_LENGTH}</span>
                </label>
                <textarea
                  rows={4}
                  value={message}
                  maxLength={MESSAGE_MAX_LENGTH}
                  onChange={e => setMessage(e.target.value)}
                  placeholder="e.g. You RSVP'd — grab your ticket before they sell out."
                />
              </div>
              <div className="form-group row">
                <label>Skip people who already bought a ticket</label>
                <ToggleInput value={excludeTicketHolders} onChange={() => setExcludeTicketHolders(v => !v)} />
              </div>
              <div className="form-group row">
                <label>Include internal / test users</label>
                <ToggleInput value={includeInternal} onChange={() => setIncludeInternal(v => !v)} />
              </div>
            </form>
            <div className="modal-footer-content">
              {selectedEvent && (
                <p className="recipient-count">
                  {isLoadingAudience ? 'Counting RSVPs…' : breakdown}
                </p>
              )}
              <button className="styled-button" onClick={() => setStage('confirm')} disabled={!canContinue}>
                {isLoadingAudience || !audience
                  ? 'Send'
                  : recipientCount === 0
                    ? 'No one to send to'
                    : `Send to ${plural(recipientCount, 'RSVP')}`}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

export default EventRsvpBlastModal
